import React, { useState, useEffect, useRef, useCallback } from 'react';
import { WebRTCManager, VoiceChatState } from '../utils/webrtc';
import { AudioQualityMetrics, ConnectionStats } from '../utils/audioQuality';
import './VoiceChatControls.css';

interface VoiceChatControlsProps {
  webrtcManager: WebRTCManager | null;
  isMatched: boolean;
  matchedUser: {
    userId: string;
    username: string;
    avatar?: string;
  } | null;
  onStartVoiceChat: () => void;
  onEndVoiceChat: () => void;
  timeRemaining: number;
}

const VoiceChatControls: React.FC<VoiceChatControlsProps> = ({
  webrtcManager,
  isMatched,
  matchedUser,
  onStartVoiceChat,
  onEndVoiceChat,
  timeRemaining
}) => {
  const [voiceState, setVoiceState] = useState<VoiceChatState>({
    isConnected: false,
    isMuted: false,
    isSpeaking: false,
    connectionQuality: 'disconnected'
  });
  
  const [isVoiceChatActive, setIsVoiceChatActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [audioPermission, setAudioPermission] = useState<boolean | null>(null);
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [audioQuality, setAudioQuality] = useState<AudioQualityMetrics | null>(null);
  const [connectionStats, setConnectionStats] = useState<ConnectionStats | null>(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);

  const initializeAudio = useCallback(async () => {
    if (!webrtcManager || audioInitialized) {
      return;
    }

    setIsConnecting(true);
    setAudioError(null);
    try {
      const success = await webrtcManager.initialize();
      setAudioPermission(success);
      setAudioInitialized(true);
      if (success) {
        console.log('音声機能の初期化が完了しました');
      } else {
        console.error('マイクアクセスの許可が必要です');
        setAudioError('マイクアクセスの許可が必要です。ブラウザの設定を確認してください。');
      }
    } catch (error: any) {
      console.error('音声初期化エラー:', error);
      setAudioPermission(false);
      setAudioInitialized(true);

      // エラーメッセージをユーザーフレンドリーに変換
      let errorMessage = 'マイクアクセスの初期化に失敗しました。';
      if (error.message?.includes('HTTPS')) {
        errorMessage = 'HTTPS接続が必要です。モバイルブラウザではHTTPSでのみマイクアクセスが許可されます。';
      } else if (error.message?.includes('タイムアウト')) {
        errorMessage = 'マイクアクセスのタイムアウト。ブラウザの許可ダイアログで「許可」を選択してください。';
      } else if (error.message?.includes('このブラウザ')) {
        errorMessage = 'このブラウザはマイクアクセスに対応していません。ChromeやSafariなどの最新ブラウザをお使いください。';
      } else if (error.name === 'NotAllowedError') {
        errorMessage = 'マイクアクセスが拒否されました。ブラウザのアドレスバーにある🔒アイコンをクリックして、マイクを「許可」に設定してください。';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'マイクが見つかりません。マイクが接続されているか確認してください。';
      }

      setAudioError(errorMessage);
    } finally {
      setIsConnecting(false);
    }
  }, [webrtcManager, audioInitialized]);

  useEffect(() => {
    if (!webrtcManager) {
      return;
    }

    // 音声状態変更の監視
    webrtcManager.setOnConnectionStateChange((state) => {
      setVoiceState(state);
    });

    // リモートストリームの処理
    webrtcManager.setOnRemoteStream((stream) => {
      console.log('🎧 リモートストリームを受信しました:', stream);
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = stream;
        remoteAudioRef.current.volume = 1.0;
        remoteAudioRef.current.muted = false;

        // ブラウザの自動再生ポリシーに対応するため、少し遅延してplayを試行
        setTimeout(() => {
          if (remoteAudioRef.current) {
            remoteAudioRef.current.play().catch((error) => {
              console.warn('自動再生に失敗しました。ユーザー操作が必要です:', error.message);
              console.log('🔊 音声再生を有効化するには、画面上の「🔊 音声を有効化」ボタンをクリックしてください');
              setAudioEnabled(false);
            });
          }
        }, 500); // 遅延時間を500msに増加

        console.log('✅ audio要素にストリームを設定しました');
      } else {
        console.error('❌ remoteAudioRefがnullです');
      }
    });

    // 音声状態変更の処理
    webrtcManager.setOnAudioStateChange((isMuted) => {
      setVoiceState(prev => ({ ...prev, isMuted }));
    });

    // 音声品質監視
    webrtcManager.setOnQualityChange((quality) => {
      setAudioQuality(quality);
      setVoiceState(prev => ({ ...prev, connectionQuality: quality.quality }));
    });

    // 接続統計情報監視
    webrtcManager.setOnConnectionStats((stats) => {
      setConnectionStats(stats);
    });

    // 初期化は手動で行うため、ここでは実行しない
  }, [webrtcManager]);

  // マッチングしたら自動的にWebRTCを初期化
  useEffect(() => {
    if (isMatched && matchedUser && !audioInitialized) {
      console.log('マッチング検知 - WebRTC初期化を開始します');
      initializeAudio();
    }
  }, [isMatched, matchedUser, audioInitialized, initializeAudio]);

  // マッチングしたら強制的にボイスチャット開始
  useEffect(() => {
    if (isMatched && matchedUser && !isVoiceChatActive) {
      console.log('マッチング検知 - 即座にボイスチャットを開始します');
      // 少し遅延して確実に開始
      const timer = setTimeout(() => {
        if (!isVoiceChatActive) {
          onStartVoiceChat();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isMatched, matchedUser, isVoiceChatActive, onStartVoiceChat]);

  const handleStartVoiceChat = async () => {
    if (!webrtcManager || !matchedUser) {
      return;
    }

    setIsConnecting(true);
    try {
      onStartVoiceChat();
      setIsVoiceChatActive(true);
      console.log('ボイスチャットを開始しました');
    } catch (error) {
      console.error('ボイスチャット開始エラー:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleEndVoiceChat = () => {
    if (!webrtcManager) {
      return;
    }

    webrtcManager.endVoiceChat();
    setIsVoiceChatActive(false);
    onEndVoiceChat();
    console.log('ボイスチャットを終了しました');
  };

  const handleToggleMute = () => {
    if (!webrtcManager) {
      return;
    }

    const isUnmuted = webrtcManager.toggleMute();
    setVoiceState(prev => ({ ...prev, isMuted: !isUnmuted }));
  };

  const enableAudioPlayback = useCallback(() => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.volume = 1.0;
      remoteAudioRef.current.muted = false;
      remoteAudioRef.current.play().catch(console.error);
      setAudioEnabled(true);
      console.log('🔊 音声再生を有効化しました');
    }
  }, []);

  const getConnectionQualityColor = (quality: string) => {
    switch (quality) {
      case 'excellent': return '#4CAF50';
      case 'good': return '#FF9800';
      case 'poor': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getConnectionQualityText = (quality: string) => {
    switch (quality) {
      case 'excellent': return '接続良好';
      case 'good': return '接続中';
      case 'poor': return '接続不良';
      default: return '未接続';
    }
  };


  return (
    <div>
      {/* リモート音声の再生 */}
      <audio
        ref={remoteAudioRef}
        autoPlay
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default VoiceChatControls;
